/**
 * "Running now" selector — the sub-tasks actively being worked right now, shared
 * by the internal Overview tab and the client-facing report so both surfaces
 * agree on what counts as "running" and in what order.
 */

/** A sub-task surfaced in the "running now" highlight. */
export interface RunningTask {
  id: string;
  name: string;
  trackLabel: string;
  progressPct: number;
  status: string;
  scheduledEnd: string | null;
  onCriticalPath: boolean;
}

/**
 * Minimal track/item shape the selector needs. Both the internal dashboard
 * (ProjectTrack) and the sanitized client payload (ShareTrack) map onto it — the
 * client payload already merges computedStatus into `status`, and the internal
 * caller passes `computedStatus ?? status`.
 */
export interface RunningTaskTrack {
  label: string;
  items: ReadonlyArray<{
    id: string;
    name: string;
    status?: string;
    progressPct?: number;
    scheduledEnd?: string | null;
  }>;
}

/**
 * A sub-task is "running" when work is underway but not finished: it has partial
 * progress (0 < % < 100) OR is explicitly flagged in_progress. Date-urgency
 * states (at_risk/late) don't gate this — a late task you're actively working is
 * still "what I'm doing now"; they only colour it. A 0%, untouched-but-overdue
 * task is NOT running (you're not working it, it's just late).
 */
export function isRunning(status: string | undefined, progressPct: number): boolean {
  if (progressPct >= 100 || status === 'complete') return false;
  return progressPct > 0 || status === 'in_progress';
}

/**
 * The top N sub-tasks being worked right now, most important first:
 *   1. on the critical path (a slip here slips the whole project),
 *   2. soonest scheduled finish (most urgent; undated sorts last),
 *   3. furthest along (closest to done).
 */
export function topRunningTasks(
  tracks: ReadonlyArray<RunningTaskTrack>,
  criticalPath: ReadonlyArray<string> = [],
  limit = 3,
): RunningTask[] {
  const cp = new Set(criticalPath);
  const running: RunningTask[] = [];
  for (const t of tracks) {
    for (const i of t.items) {
      const pct = i.progressPct ?? 0;
      if (!isRunning(i.status, pct)) continue;
      running.push({
        id: i.id,
        name: i.name,
        trackLabel: t.label,
        progressPct: pct,
        status: i.status ?? 'in_progress',
        scheduledEnd: i.scheduledEnd ?? null,
        onCriticalPath: cp.has(i.id),
      });
    }
  }
  running.sort((a, b) => {
    if (a.onCriticalPath !== b.onCriticalPath) return a.onCriticalPath ? -1 : 1;
    if (a.scheduledEnd !== b.scheduledEnd) {
      if (!a.scheduledEnd) return 1;
      if (!b.scheduledEnd) return -1;
      return a.scheduledEnd < b.scheduledEnd ? -1 : 1;
    }
    return b.progressPct - a.progressPct;
  });
  return running.slice(0, limit);
}
